import { Component, effect, inject, model, resource, signal, untracked } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { NgSelectModule } from "@ng-select/ng-select";
import { firstValueFrom, map } from "rxjs";
import { ArticleService } from "src/app/_services";

export type ArticleSearchParams = {
  status_id?: string;
  product_id?: number;
  rma?: string;
  serial_number?: string;
  // RFID-Etikett (bentomax/hawk#605): XID/EPC exakt, sonst Teiltreffer; Lagerort-Label Teiltreffer
  xid?: string;
  shelf_label?: string;
};

@Component({
  selector: "hawk-artikel-search-params",
  template: `
    <fieldset class="bm-fieldset">
      <label class="bm-fieldset-label">Status</label>
      <select class="bm-select" [formControl]="form.controls.status_id">
        @for (option of statusList(); track $index) {
          <option [value]="option.id">{{ option.description }}</option>
        }
      </select>

      <label class="bm-fieldset-label">Produkt</label>
      <ng-select
        [items]="products.value()"
        bindLabel="description"
        bindValue="id"
        [formControl]="form.controls.product_id"
        placeholder="Produkt"
        [loading]="products.isLoading()"
      >
        <ng-template ng-label-tmp let-item="item">
          {{ item.description }}
        </ng-template>
        <ng-template ng-option-tmp let-item="item" let-search="searchTermStatus">
          @if (!search || item.description.toLowerCase().includes(search.toLowerCase())) {
            {{ item.description }}
          }
        </ng-template>
      </ng-select>

      <label class="bm-fieldset-label"> Seriennummer </label>
      <input class="bm-input" type="text" [formControl]="form.controls.serial_number" (keydown)="$event.stopPropagation()" />

      <label class="bm-fieldset-label" style="display: inline-flex"> RMA </label>
      <input class="bm-input" type="text" [formControl]="form.controls.rma" (keydown)="$event.stopPropagation()" />

      <label class="bm-fieldset-label"> XID / EPC </label>
      <input class="bm-input" type="text" [formControl]="form.controls.xid" (keydown)="$event.stopPropagation()" />

      <label class="bm-fieldset-label"> Lagerort </label>
      <input class="bm-input" type="text" [formControl]="form.controls.shelf_label" (keydown)="$event.stopPropagation()" />
    </fieldset>
  `,
  host: {
    class: "block",
  },
  imports: [NgSelectModule, FormsModule, ReactiveFormsModule],
})
export class ArtikelSearchParamsComponent {
  public params = model<ArticleSearchParams>();

  private builder = inject(FormBuilder);
  private articleService = inject(ArticleService);

  protected statusFilter = signal("");
  protected statusList = signal([
    { id: "", description: "Alle Artikel" },
    { id: "WAREHOUSE", description: "Artikel im Lager" },
    { id: "SHIPPING", description: "Artikel im Versand" },
    { id: "CONSUMED", description: "Verbrauchte Artikel" },
  ]);

  protected form = this.builder.group({
    status_id: this.builder.control<string>({ value: "", disabled: false }),
    product_id: this.builder.control<number>({ value: null, disabled: false }),
    rma: this.builder.control<string>({ value: "", disabled: false }),
    serial_number: this.builder.control<string>({ value: "", disabled: false }),
    xid: this.builder.control<string>({ value: "", disabled: false }),
    shelf_label: this.builder.control<string>({ value: "", disabled: false }),
  });

  protected products = resource({
    loader: () => {
      return firstValueFrom(
        this.articleService.loadArticleTemplates().pipe(
          map((data) => {
            return data.sort((a, b) => a.description.localeCompare(b.description));
          }),
        ),
      );
    },
  });

  public constructor() {
    effect(() => {
      const params = this.params();
      if (params?.product_id) {
        params.product_id = Number(params.product_id);
      }
      untracked(() => {
        this.form.patchValue(
          {
            product_id: params?.product_id || null,
            status_id: params?.status_id || "",
            rma: params?.rma || "",
            serial_number: params?.serial_number || "",
            xid: params?.xid || "",
            shelf_label: params?.shelf_label || "",
          },
          {
            emitEvent: false,
          },
        );
      });
    });

    this.form.valueChanges
      .pipe(
        map((params) => {
          const result: ArticleSearchParams = {};
          for (const key in params || {}) {
            if (params[key]) {
              result[key] = params[key];
            }
          }
          return result;
        }),
        takeUntilDestroyed(),
      )
      .subscribe((params) => {
        this.params.set(params);
      });
  }
}
